import { useState } from "react";
import { api, useAction, useApi } from "../../api.ts";
import { fmtBytes, fmtDateTime, fmtRelative, shortHash } from "../../format.ts";
import {
  Badge, Button, Card, CopyButton, Dialog, EmptyState, ErrorText, Field, Input, Loading, LoadError, Mono, PageHeader, Select, Table,
  TBody, Td, Textarea, Th, THead, Tr, useToast,
} from "../../ui/index.tsx";
import { FormActions } from "./_ops.tsx";
import type { FileInventory, LoaderVersion } from "../../../shared/types.ts";

export default function Releases() {
  const versions = useApi<LoaderVersion[]>("/admin/loader-versions");
  const files = useApi<FileInventory>("/admin/files");
  const [publishing, setPublishing] = useState(false);

  const active = versions.data?.find((v) => v.active);
  const rootFiles = files.data?._root?.files.map((f) => f.name) ?? [];

  return (
    <>
      <PageHeader
        title="Releases"
        description="Loader builds and the files served to clients."
        actions={<Button variant="primary" icon="package" onClick={() => setPublishing(true)}>Publish version</Button>}
      />

      <div className="space-y-6">
        <Card title="Active version" description="What loaders download when an update is available.">
          {!versions.data ? (
            versions.loading ? <Loading /> : <LoadError error={versions.error} retry={() => void versions.reload()} />
          ) : !active ? (
            <EmptyState icon="package" title="No version published" body="Publish a loader build to start serving updates." />
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-2xl font-semibold tracking-tight">v{active.version}</span>
                <Badge tone="ok" dot>Active</Badge>
                <span className="text-sm text-muted" title={fmtDateTime(active.created_at)}>Published {fmtRelative(active.created_at)}</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                <span className="flex items-center gap-2"><span className="text-xs text-faint">File</span><Mono>{active.filename}</Mono></span>
                <span className="flex items-center gap-1"><span className="text-xs text-faint">SHA-256</span><Mono className="break-all">{active.sha256}</Mono><CopyButton text={active.sha256} label="Copy SHA-256" /></span>
              </div>
              {active.changelog && <p className="whitespace-pre-line rounded-lg bg-bg p-3 text-sm text-muted">{active.changelog}</p>}
            </div>
          )}
        </Card>

        <Card title="Version history" padded={false}>
          {!versions.data ? (
            versions.loading ? <Loading /> : null
          ) : versions.data.length === 0 ? (
            <EmptyState icon="clock" title="No versions yet" />
          ) : (
            <Table>
              <THead><tr><Th>Version</Th><Th>File</Th><Th>SHA-256</Th><Th>Changelog</Th><Th>Published</Th><Th /></tr></THead>
              <TBody>
                {versions.data.map((v) => (
                  <Tr key={v.id}>
                    <Td className="font-medium">v{v.version}</Td>
                    <Td><Mono>{v.filename}</Mono></Td>
                    <Td><span className="inline-flex items-center gap-1"><Mono>{shortHash(v.sha256)}</Mono><CopyButton text={v.sha256} label="Copy SHA-256" /></span></Td>
                    <Td className="max-w-xs truncate text-muted"><span title={v.changelog ?? undefined}>{v.changelog || "—"}</span></Td>
                    <Td className="whitespace-nowrap text-muted"><span title={fmtDateTime(v.created_at)}>{fmtRelative(v.created_at)}</span></Td>
                    <Td>{v.active && <Badge tone="ok" dot>Active</Badge>}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </Card>

        <Card
          title="File inventory"
          description="Drop files into files/<game-id>/ or files/shared/ on the server. Loader builds go in the files root. Nothing is uploaded from here."
          actions={<Button size="sm" icon="refresh" onClick={() => void files.reload()} loading={files.loading && !!files.data}>Rescan</Button>}
          padded={false}
        >
          {!files.data ? (
            files.loading ? <Loading /> : <LoadError error={files.error} retry={() => void files.reload()} />
          ) : Object.keys(files.data).length === 0 ? (
            <EmptyState icon="package" title="No files found" body="The server's files folder is empty." />
          ) : (
            <div className="divide-y divide-line">
              {Object.entries(files.data).map(([group, g]) => (
                <div key={group}>
                  <div className="flex items-center gap-2 bg-bg/40 px-5 py-2.5">
                    <span className="text-sm font-medium">{g.name}</span>
                    <Mono>{group === "_root" ? "files/" : `files/${group}/`}</Mono>
                    <span className="ml-auto text-xs text-faint">{g.files.length} {g.files.length === 1 ? "file" : "files"}</span>
                  </div>
                  {g.files.length === 0 ? (
                    <p className="px-5 py-3 text-sm text-faint">No files.</p>
                  ) : (
                    <Table>
                      <THead><tr><Th>Name</Th><Th>Size</Th><Th>Modified</Th><Th>SHA-256</Th></tr></THead>
                      <TBody>
                        {g.files.map((f) => (
                          <Tr key={f.name}>
                            <Td><span className="inline-flex items-center gap-2"><Mono className="text-fg">{f.name}</Mono>{f.shared && <Badge tone="info">shared</Badge>}</span></Td>
                            <Td className="whitespace-nowrap tabular-nums text-muted">{fmtBytes(f.size)}</Td>
                            <Td className="whitespace-nowrap text-muted"><span title={fmtDateTime(f.modified)}>{fmtRelative(f.modified)}</span></Td>
                            <Td><span className="inline-flex items-center gap-1"><Mono>{shortHash(f.sha256)}</Mono><CopyButton text={f.sha256} label="Copy SHA-256" /></span></Td>
                          </Tr>
                        ))}
                      </TBody>
                    </Table>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Dialog open={publishing} onClose={() => setPublishing(false)} title="Publish loader version"
        description="The file must already be in the server's files folder. It becomes the active version and connected loaders are notified.">
        <PublishForm
          rootFiles={rootFiles}
          onClose={() => setPublishing(false)}
          onPublished={() => { setPublishing(false); void versions.reload(); void files.reload(); }}
        />
      </Dialog>
    </>
  );
}

function PublishForm({ rootFiles, onClose, onPublished }: { rootFiles: string[]; onClose: () => void; onPublished: () => void }) {
  const toast = useToast();
  const [version, setVersion] = useState("");
  const [filename, setFilename] = useState(rootFiles[0] ?? "");
  const [changelog, setChangelog] = useState("");

  const publish = useAction(async () => {
    const res = await api.post<{ id: number; sha256: string }>("/admin/loader-versions", {
      version: version.trim(), filename: filename.trim(), ...(changelog.trim() ? { changelog: changelog.trim() } : {}),
    });
    toast(`Published v${version.trim()} (${shortHash(res.sha256, 8)})`, "success");
    onPublished();
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); void publish.run(); }} className="space-y-4">
      <Field label="Version" hint="e.g. 1.4.2"><Input value={version} onChange={(e) => setVersion(e.target.value)} required autoFocus /></Field>
      <Field label="Filename" hint={rootFiles.length ? "Files in the server's files root" : "No files found in the files root; type the filename"}>
        {rootFiles.length ? (
          <Select value={filename} onChange={(e) => setFilename(e.target.value)}>
            {rootFiles.map((n) => <option key={n} value={n}>{n}</option>)}
          </Select>
        ) : (
          <Input value={filename} onChange={(e) => setFilename(e.target.value)} className="font-mono" required />
        )}
      </Field>
      <Field label="Changelog" hint="Optional. Sent to loaders with the update notification.">
        <Textarea value={changelog} onChange={(e) => setChangelog(e.target.value)} rows={4} />
      </Field>
      <ErrorText>{publish.error}</ErrorText>
      <FormActions onCancel={onClose} submitLabel="Publish" icon="package" loading={publish.pending} disabled={!version.trim() || !filename.trim()} />
    </form>
  );
}
